<template>
  <div class="contact__header">
    <p>{{ $t('CHAT.CHATS') }}</p>
    <ChatIcon></ChatIcon>
  </div>
  <form class="form__container" @submit.prevent="() => null">
    <input
      v-model="searchedQuery"
      type="text"
      :placeholder="$t('CHAT.SEARCH_CONTACT')"
      name="search"
      class="form__container--search" />
    <button type="submit" class="btn form__container--button">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="28"
        height="28"
        viewBox="0 0 28 28"
        fill="none">
        <g clip-path="url(#clip0_2017_5028)">
          <path
            d="M26.6358 25.0927L20.2098 18.4093C21.862 16.4452 22.7673 13.9739 22.7673 11.4012C22.7673 5.39039 17.8769 0.5 11.8661 0.5C5.85523 0.5 0.964844 5.39039 0.964844 11.4012C0.964844 17.4121 5.85523 22.3025 11.8661 22.3025C14.1226 22.3025 16.273 21.6219 18.1115 20.3298L24.5864 27.064C24.857 27.345 25.221 27.5 25.6111 27.5C25.9803 27.5 26.3306 27.3592 26.5965 27.1033C27.1615 26.5597 27.1795 25.6582 26.6358 25.0927ZM11.8661 3.3438C16.309 3.3438 19.9235 6.95827 19.9235 11.4012C19.9235 15.8442 16.309 19.4587 11.8661 19.4587C7.42312 19.4587 3.80865 15.8442 3.80865 11.4012C3.80865 6.95827 7.42312 3.3438 11.8661 3.3438Z"
            fill="white" />
        </g>
        <defs>
          <clipPath id="clip0_2017_5028">
            <rect
              width="27"
              height="27"
              fill="white"
              transform="translate(0.5 0.5)" />
          </clipPath>
        </defs>
      </svg>
    </button>
  </form>
  <div class="contact__list">
      <div class="box__user ai" @click="openAIChat()" v-if="isUserRole(UserRole.CLIENT)">
          <div class="box__user--icon">
              <AvailableIcon></AvailableIcon>
          </div>
          <div class="box__user--name">
              <p>{{ $t('AI_CHAT.TITLE') }}</p>
          </div>
          <div class="box__user--company">
              <p v-if="aiChatData?.lastMessage">
                  {{
                      aiChatData.lastMessage.length > 20
                              ? aiChatData.lastMessage.slice(0, 20) + '...'
                              : aiChatData.lastMessage
                  }}
              </p>
          </div>
      </div>
    <div v-if="isLoading" class="grid h-full place-items-center">
      <Loader />
    </div>
    <TransitionGroup
      v-else-if="!isLoading && filteredChatList.length"
      name="fade-left"
      tag="ul">
      <li
        v-for="chatData in filteredChatList"
        :key="'chat__' + chatData._id"
        class="box__user"
        @click="openChat(chatData)">
        <div class="box__user--img mask-img">
          <img v-if="chatData.avatar" :src="chatData.avatar" alt="" />
          <img
            v-else
            src="@/assets/img/profile/profile_initial_photo.png"
            alt="" />
        </div>
        <div class="box__user--name">
          <p>{{ chatData.name }}</p>
        </div>
        <div class="box__user--company">
          <p v-if="chatData.lastMessage">
            {{
              chatData.lastMessage.length > 20
                ? chatData.lastMessage.slice(0, 20) + '...'
                : chatData.lastMessage
            }}
          </p>
        </div>
        <div class="box__user--updated-at">
          {{ getChatUpdateDate(chatData.updatedAt) }}
        </div>
        <div class="box__user--icon">
          <template v-if="isParticipantAvailable(chatData.participants)">
            <AvailableIcon />
          </template>
          <template v-else>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="13"
              viewBox="0 0 11 13"
              fill="none">
              <path
                d="M7.78279 5.99274L1.72295 0.207681C1.5828 0.0737718 1.3957 0 1.1962 0C0.996706 0 0.809609 0.0737718 0.669453 0.207681L0.223188 0.633613C-0.0671998 0.911155 -0.0671998 1.36224 0.223188 1.63936L5.31178 6.4973L0.217542 11.3606C0.0773851 11.4945 0 11.6731 0 11.8634C0 12.054 0.0773851 12.2325 0.217542 12.3665L0.663807 12.7923C0.804074 12.9262 0.99106 13 1.19056 13C1.39005 13 1.57715 12.9262 1.71731 12.7923L7.78279 7.00198C7.92328 6.86764 8.00044 6.68829 8 6.49762C8.00044 6.30622 7.92328 6.12697 7.78279 5.99274Z"
                fill="#353535" />
            </svg>
          </template>
        </div>
      </li>
    </TransitionGroup>
    <div v-else class="text-center">{{ $t('CHAT.NO_CHATS') }}</div>
  </div>
</template>
<script setup lang="ts">
import { useAuthStore } from '~/stores/authStore'
import Loader from '~/components/shared/Loader.vue'
import { promiseWithTimeout } from '~/utils'
import moment from 'moment'
import { AppEvents } from '~/types/events'
import { useChatStore } from '~/stores/chatStore'
import { findChatRoomByRoomId } from '~/composables/utils'
import AvailableIcon from '~/assets/img/common/available.svg?skipsvgo'
import ChatIcon from '~/assets/img/icons/chat-button.svg?skipsvgo'
import {storeToRefs} from "pinia";
import {isUserRole} from '~/composables/utils'
import {UserRole} from '~/types/userRole'

const localePath = useLocalePath()
const router = useRouter()

const { userInfo } = storeToRefs(useUserStore())
const chatStore = useChatStore()
const { chatList, selectedChatData, roomId, aiChatData, currentView } = storeToRefs(chatStore)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { userId } = storeToRefs(useAuthStore())

const isLoading = ref(true)
const page = ref(1)

function getChatUpdateDate(dateTime) {
  const isToday = moment().isSame(dateTime, 'day')

  return moment
    .utc(dateTime)
    .local()
    .format(isToday ? 'HH:mm' : 'YYYY/MM/DD HH:mm')
}
function openAIChat() {
    currentView.value = 'chat'
    router.push(localePath(`/chat/ai`))
}

function openChat(chatData: any) {
  selectedChatData.value = findChatRoomByRoomId(
    chatList.value,
    String(chatData._id)
  )

  if (!selectedChatData.value) {
    return
  }

  currentView.value = 'chat'
  const roomId = selectedChatData.value?._id
  window.location.href = `/${locale.value}/chat/${roomId}`
}

const { fetchChats } = useUserStore()

const { $listen, $socketUtils } = useNuxtApp()

const { locale } = useI18n()

$listen(AppEvents.FETCH_CHATS, async () => {
  await promiseWithTimeout(await chatStore.fetchChats(), 3000)
})

function isParticipantAvailable(participants) {
  const MAX_USER_CHAT_AVAILABILITY = 10

  const currentTime = new Date().toISOString()
  const participant = participants.find(
    p => p.cognitoUserId !== userInfo.value.userId
  )
  if (participant.lastActiveAt !== undefined) {

    const difference = moment.duration(
      moment(currentTime).diff(moment(participant.lastActiveAt))
    )

    console.log(difference.asMinutes())

    return difference.asMinutes() <= MAX_USER_CHAT_AVAILABILITY
  } else {
    return false
  }
}

const searchedQuery = ref('')

const filteredChatList = computed({
  get: function () {
    if (searchedQuery.value) {
      return chatList.value.filter(cl =>
        cl.name.toLowerCase().includes(searchedQuery.value.toLowerCase())
      )
    }
    return chatList.value
  },
  set() {},
})
const getChats = async () => {
  try {
    isLoading.value = true
    await promiseWithTimeout(await chatStore.fetchChats(), 5000)
  } catch (e) {
    console.log(e)
  } finally {
    isLoading.value = false
  }
}


const setSelectedChat = ref()

onMounted(async () => {
  const route = useRoute()

  setSelectedChat.value = setInterval(() => {
    if (route.params.id && chatList.value.length) {
      roomId.value = String(route.params.id)
      findChatRoomByRoomId(chatList.value, String(route.params.id))

      selectedChatData.value = findChatRoomByRoomId(
        chatList.value,
        String(route.params.id)
      )
      clearInterval(setSelectedChat.value)
    }
  }, 1000)

    await getChats()
})

onUnmounted(() => {
  clearInterval(setSelectedChat.value)
})

watch(page, async () => {
  await getChats()
})
</script>
<style lang="scss" scoped>
.box {
  &__user {
    gap: 5px;
    grid-template-areas:
      'img name name icon'
      'img company dateTime icon' !important;
    &--img {
      min-width: 60px;
    }
    &--updated-at {
      text-align: right;
      grid-area: dateTime;
      font-size: 16px;
      color: rgba(53, 53, 53, 0.5);
    }
    &.ai {
      padding: 20px 30px;
      grid-template-columns: auto 1fr;
      grid-template-rows: 1fr;
      grid-template-areas:
      'icon name'
      'company company'!important;
      box-shadow: inset 0px -10px 24px 5px #3870FF33;
      border-radius: 20px;
      min-height: 95px;
      justify-items: center;
    }
  }
}
</style>
